import {
  collection,
  deleteDoc,
  doc,
  getDocs,
  setDoc,
  updateDoc,
  type CollectionReference,
  type DocumentData,
  type DocumentReference,
} from "firebase/firestore";

import { db } from "@/apis/config/firebase";
import { SYSTEM_ROLES } from "@/features/rbac/permissions/systemRoles";
import {
  ROLE_ID_PATTERN,
  createRoleSchema,
  rbacRoleSchema,
  updateRoleSchema,
  type CreateRoleInput,
  type RbacRole,
  type UpdateRoleInput,
} from "@/features/rbac/schemas/rbac";
import {
  RbacPolicyError,
  assertBackstopRoleIntact,
  assertRoleIsDeletable,
} from "@/features/rbac/utils/policy";

export const RBAC_ROLES_COLLECTION = "rbac_roles";

function getRolesCollection(): CollectionReference<DocumentData> {
  return collection(db, RBAC_ROLES_COLLECTION);
}

function getRoleDoc(roleId: string): DocumentReference<DocumentData> {
  return doc(db, RBAC_ROLES_COLLECTION, roleId);
}

/** Convierte un Timestamp de Firestore (o una fecha suelta) en Date. */
function toDate(value: unknown): Date | null {
  if (!value) return null;
  if (value instanceof Date) return value;
  if (typeof value === "object" && "toDate" in value) {
    const candidate = (value as { toDate: () => Date }).toDate();
    return candidate instanceof Date ? candidate : null;
  }
  return null;
}

/**
 * Normaliza un documento crudo. Los permisos desconocidos se descartan en el
 * schema en lugar de invalidar el rol completo.
 */
function mapRole(id: string, data: DocumentData): RbacRole | null {
  const parsed = rbacRoleSchema.safeParse({
    id,
    name: typeof data.name === "string" ? data.name : id,
    description: typeof data.description === "string" ? data.description : "",
    permissions: Array.isArray(data.permissions) ? data.permissions : [],
    system: data.system === true,
    legacyKey: typeof data.legacy_key === "string" ? data.legacy_key : null,
    updatedAt: toDate(data.updated_at),
    updatedBy: typeof data.updated_by === "string" ? data.updated_by : null,
  });

  return parsed.success ? parsed.data : null;
}

/**
 * Deriva un id estable a partir del nombre del rol. El id es inmutable una vez
 * creado, porque las asignaciones apuntan a él.
 */
export function slugifyRoleId(name: string): string {
  const slug = name
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9]+/g, "_")
    .replace(/^_+|_+$/g, "")
    .slice(0, 50);

  if (!ROLE_ID_PATTERN.test(slug)) {
    throw new RbacPolicyError(
      "El nombre del rol debe contener al menos dos caracteres alfanuméricos."
    );
  }

  return slug;
}

/** Lista todos los roles, ordenados con los del sistema primero. */
export async function listRoles(): Promise<RbacRole[]> {
  const snapshot = await getDocs(getRolesCollection());

  return snapshot.docs
    .map((docSnap) => mapRole(docSnap.id, docSnap.data()))
    .filter((role): role is RbacRole => role !== null)
    .sort((a, b) => {
      if (a.system !== b.system) return a.system ? -1 : 1;
      return a.name.localeCompare(b.name, "es");
    });
}

function assertNameIsFree(
  roles: readonly RbacRole[],
  name: string,
  excludeRoleId?: string
): void {
  const clash = roles.find(
    (role) => role.name.toLowerCase() === name.toLowerCase() && role.id !== excludeRoleId
  );
  if (clash) {
    throw new RbacPolicyError(`Ya existe un rol con el nombre "${name}".`);
  }
}

export async function createRole(
  input: CreateRoleInput,
  actorUid: string | null
): Promise<RbacRole> {
  const parsed = createRoleSchema.parse(input);
  const existing = await listRoles();

  assertNameIsFree(existing, parsed.name);

  const id = slugifyRoleId(parsed.name);
  if (existing.some((role) => role.id === id)) {
    throw new RbacPolicyError(
      `El identificador "${id}" ya está en uso. Elige un nombre distinto.`
    );
  }

  await setDoc(getRoleDoc(id), {
    name: parsed.name,
    description: parsed.description,
    permissions: parsed.permissions,
    system: false,
    legacy_key: null,
    created_at: new Date(),
    updated_at: new Date(),
    updated_by: actorUid,
  });

  return {
    id,
    name: parsed.name,
    description: parsed.description,
    permissions: parsed.permissions,
    system: false,
    legacyKey: null,
    updatedAt: new Date(),
    updatedBy: actorUid,
  };
}

export async function updateRole(
  roleId: string,
  input: UpdateRoleInput,
  actorUid: string | null
): Promise<void> {
  const parsed = updateRoleSchema.parse(input);
  const existing = await listRoles();
  const role = existing.find((candidate) => candidate.id === roleId);

  if (!role) {
    throw new RbacPolicyError("El rol que intentas editar ya no existe.");
  }

  const payload: Record<string, unknown> = {};

  if (parsed.name !== undefined && parsed.name !== role.name) {
    assertNameIsFree(existing, parsed.name, roleId);
    payload.name = parsed.name;
  }

  if (parsed.description !== undefined) {
    payload.description = parsed.description;
  }

  if (parsed.permissions !== undefined) {
    assertBackstopRoleIntact(roleId, parsed.permissions);
    payload.permissions = parsed.permissions;
  }

  if (Object.keys(payload).length === 0) return;

  payload.updated_at = new Date();
  payload.updated_by = actorUid;

  await updateDoc(getRoleDoc(roleId), payload);
}

export async function deleteRole(roleId: string): Promise<void> {
  const existing = await listRoles();
  const role = existing.find((candidate) => candidate.id === roleId);

  if (!role) return;

  assertRoleIsDeletable(role);
  await deleteDoc(getRoleDoc(roleId));
}

/**
 * Crea los roles del sistema que falten. Es idempotente y NUNCA sobrescribe uno
 * existente: un administrador puede haber editado legítimamente `support`, y
 * esa edición debe sobrevivir a cualquier resiembra.
 *
 * @returns los ids que se crearon en esta llamada.
 */
export async function ensureSystemRoles(actorUid: string | null): Promise<string[]> {
  const existing = await listRoles();
  const existingIds = new Set(existing.map((role) => role.id));
  const missing = SYSTEM_ROLES.filter((role) => !existingIds.has(role.id));

  if (missing.length === 0) return [];

  await Promise.all(
    missing.map((role) =>
      setDoc(getRoleDoc(role.id), {
        name: role.name,
        description: role.description,
        permissions: [...role.permissions],
        system: true,
        legacy_key: role.id,
        created_at: new Date(),
        updated_at: new Date(),
        updated_by: actorUid,
      })
    )
  );

  return missing.map((role) => role.id);
}
